import apiClient from '@shared/services/apiClient';
import type { Customer, CustomerLocation } from '../types/clientClinic';
import type { ClientCoverageSummary, LocationClinicDetail } from '../types/clientCoverage';

export const searchCustomers = async (query: string, limit = 20): Promise<Customer[]> => {
  const response = await apiClient.get('/customers/search', { params: { q: query, limit } });
  return response.data;
};

export interface PagedCustomers {
  data: Customer[];
  meta: {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

/** One page of customers ordered by name; blank search browses all customers. */
export const getCustomersPaged = async (
  search: string,
  page: number,
  pageSize = 50
): Promise<PagedCustomers> => {
  const response = await apiClient.get('/customers/paged', {
    params: { search: search.trim() || undefined, page, pageSize },
  });
  return response.data;
};

export const getCustomerById = async (id: number): Promise<Customer> => {
  const response = await apiClient.get(`/customers/${id}`);
  return response.data;
};

export const getCustomerLocations = async (customerId: number): Promise<CustomerLocation[]> => {
  const response = await apiClient.get(`/customers/${customerId}/locations`);
  return response.data;
};

export const getCustomerLocationById = async (locationId: number): Promise<CustomerLocation> => {
  const response = await apiClient.get(`/customers/locations/${locationId}`);
  return response.data;
};

export const searchCustomerLocations = async (
  query: string,
  customerId?: number,
  limit = 20
): Promise<CustomerLocation[]> => {
  const response = await apiClient.get('/customers/locations/search', {
    params: { q: query, customerId, limit },
  });
  return response.data;
};

export const geocodeLocation = async (
  customerId: number,
  locationId: number
): Promise<CustomerLocation> => {
  const response = await apiClient.post(`/customers/${customerId}/locations/${locationId}/geocode`);
  return response.data;
};

export const getCustomerCoverageSummary = async (
  customerId: number,
  radiusMiles: number = 25
): Promise<ClientCoverageSummary> => {
  const response = await apiClient.get(`/customers/${customerId}/coverage-summary`, {
    params: { radiusMiles },
  });
  return response.data;
};

export const getLocationClinicDetails = async (
  customerId: number,
  locationId: number,
  radiusMiles: number = 25
): Promise<LocationClinicDetail[]> => {
  const response = await apiClient.get(
    `/customers/${customerId}/locations/${locationId}/clinics`,
    { params: { radiusMiles } }
  );
  return response.data;
};
